import { Box } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import WarningAlertIcon from "../../../assets/images/icons/WarningAlertIcon";
import { SkeletonImage } from "../../../components/common/Skeleton/SkeletonGallery";
import { PlaygroundImage } from "../../../models/playground";
import useImageUtils from "../../../utils/useImageUtils";
import { ILabResultGroup } from "../../Playground/store/playground-store";
import { PlaygroundVideoProps } from "../../Playground/VideoGeneration/Video/PlaygroundVideo";
import styles from "./LabExperiments.module.scss";

type Props = {
	group: ILabResultGroup;
	isActive: boolean;
	onClick: () => void;
};

const isVideo = (item: PlaygroundImage | PlaygroundVideoProps | undefined): item is PlaygroundVideoProps => {
	return !!item && typeof (item as any)?.src === "string";
};

const Experiment = ({ group, isActive, onClick }: Props) => {
	const { generateVideoThumbnail } = useImageUtils();
	const [videoThumbnail, setVideoThumbnail] = useState<string | null>(null);
	const generatingForSrcRef = useRef<string | null>(null);

	const selectedItem = group.selectedImage;

	useEffect(() => {
		if (!selectedItem || !isVideo(selectedItem)) {
			setVideoThumbnail(null);
			generatingForSrcRef.current = null;
			return;
		}

		const video = selectedItem as PlaygroundVideoProps;

		if (video.thumbnail) {
			setVideoThumbnail(video.thumbnail);
			return;
		}

		if (!video.src?.trim() || generatingForSrcRef.current === video.src) {
			return;
		}

		generatingForSrcRef.current = video.src;
		generateVideoThumbnail(video.src)
			.then((thumbnail) => {
				if (generatingForSrcRef.current === video.src && thumbnail) {
					setVideoThumbnail(thumbnail);
				}
			})
			.catch((error) => {
				console.error("Failed to generate video thumbnail:", error);
			})
			.finally(() => {
				if (generatingForSrcRef.current === video.src) {
					generatingForSrcRef.current = null;
				}
			});
	}, [selectedItem]);

	if (selectedItem?.loading) {
		return (
			<SkeletonImage
				onClick={onClick}
				className={clsx(styles.skeletonImage, { [styles.active]: isActive })}
				aspectRatio="1:1"
			/>
		);
	}

	const imageUrl = selectedItem
		? isVideo(selectedItem)
			? videoThumbnail || (selectedItem as PlaygroundVideoProps).thumbnail
			: (selectedItem as PlaygroundImage).url
		: undefined;

	const hasError = selectedItem?.error && selectedItem?.error?.type;

	return (
		<Box className={clsx(styles.image, { [styles.active]: isActive })} onClick={onClick}>
			{imageUrl ? (
				<LazyLoadImage
					src={imageUrl}
					alt=""
					effect="blur"
					threshold={100}
					loading="lazy"
					className={clsx(styles.img, styles.transparentGridBackground)}
				/>
			) : hasError ? (
				<Box className={styles.errorIcon}>
					<WarningAlertIcon width={30} height={30} />
				</Box>
			) : null}
		</Box>
	);
};

export default observer(Experiment);
